// ProfessionalProfile.jsx
import { Fragment, Suspense, useCallback, useEffect, useState } from "react";
import axios from "axios";
// BookingForm's script loads on demand (below). Its stylesheets stay here, in
// the place and order they had when it was imported directly.
import "./booking-add-ons.css";
import "./inline-slot-picker.css";
import "./booking-form.css";
import "react-datepicker/dist/react-datepicker.css";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import Modal from "../modal/Modal.js";
import "./ProfessionalProfile.css"; // keep your page styles
import "./ProfessionalProfileUnavailable.css";
import { createSanitizedMarkup } from "../../utils/sanitizeHtml";
import { readServiceLinkId } from "../../utils/bookingPageLink";
import { getAvatarInitial, resolveAvatarUrl } from "../../utils/profileAvatar";
import { buildPublicProfileTheme } from "../../utils/publicProfileTheme";
import { loadProfileThemeFont } from "../../utils/profileThemeFontLoader";
import { getValidToken } from "../../utils/auth";
import { lazyWithReload } from "../../utils/lazyWithReload";
import {
  resolveProfessionalPaymentMode,
  resolveServicePaymentMode,
} from "../../utils/paymentMode";
import { canUseProfessionalFeatures } from "../../utils/roleCapabilities";
import { getCancellationPolicyOption } from "../../constants/cancellationPolicy";
import { SUPPORT_EMAIL } from "../../constants/supportContact";
import {
  ProfileContactSection,
  ProfileFaqSection,
  ProfileGallerySection,
  ProfileHeaderSection,
  ProfileHoursSection,
  ProfileIntroSection,
  ProfilePoliciesSection,
  ProfileServicesSection,
  buildHeroTags,
  isCustomProfileFormat,
  normalizeGalleryItems,
  normalizeIntroImages,
  renderPageSections,
  resolvePageSections,
} from "./profileSections";

// The booking form, and Stripe's card field with it, loads when a client opens
// it, so neither this page nor the marketing site downloads its script up front.
const BookingForm = lazyWithReload(() =>
  import(/* webpackChunkName: "booking-form" */ "./BookingForm.js")
);

// Both fallback pages are written for whoever followed the link, not for the
// account behind it. Neither names the account state, and neither ever shows
// the professional's name, branding or theme, so the two pages are safe to
// serve to a stranger who guessed a slug.
const AWAY_PAGE_COPY = {
  eyebrow: "Not taking bookings",
  title: "This page isn't taking bookings right now",
  lead: "The link is fine \u2014 this booking page just isn't open at the moment. It may reopen later.",
  detail:
    "Already have an appointment booked here? It isn't affected \u2014 use the link in your confirmation email to manage it.",
};

const NOT_FOUND_PAGE_COPY = {
  title: "We couldn't find this page",
  lead: "This link may be mistyped, or the page may no longer exist.",
};

// Neither page should be indexed: they are transient states of a real URL, and
// the not-found page answers guessed slugs as well as retired ones.
const FALLBACK_DOCUMENT_TITLE = "Not available \u00b7 Haastia";

// Closing the booking form forgets the booking a card attempt is holding, while
// that booking keeps the time until its hold runs out.
const HELD_BOOKING_CLOSE_CONFIRMATION =
  "Your time slot is still held for your unfinished payment. If you close now, you can’t book it again until the hold ends in a few minutes. Close anyway?";
// Closing the booking form also drops what the client has typed into it, photos
// included, so a stray Esc or tap outside the card asks first.
const UNSAVED_BOOKING_CLOSE_CONFIRMATION =
  "Close without booking? The details you’ve entered won’t be saved.";

const normalizeIdentifier = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim().toLowerCase();
};

const UnavailableInfoIcon = () => (
  <svg
    className="profile-unavailable-detail__icon"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.25"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8h.01" />
    <path d="M11.5 12h.5v4h.5" />
  </svg>
);

// The status callout keeps its own box so the steps below read as the next
// thing to do, rather than as more of the same paragraph.
const renderUnavailableDetail = (detail) =>
  detail ? (
    <div className="profile-unavailable-detail">
      <UnavailableInfoIcon />
      <p>{detail}</p>
    </div>
  ) : null;

// There is no public browse or search route in routeConfig.js, so home is the
// only onward destination a visitor can be offered. If one is ever added, this
// becomes the primary and "Go to home" drops back to the secondary button.
const renderUnavailableActions = () => (
  <div className="profile-unavailable-actions">
    <Link
      className="profile-unavailable-button profile-unavailable-button--primary"
      to="/"
    >
      Go to home
    </Link>
  </div>
);

// The owner's way back. It is a footnote rather than a button because the page
// is addressed to a visitor, for whom signing in is not a next step at all.
const renderUnavailableOwnerLink = () => (
  <p className="profile-unavailable-owner">
    Is this your page? <Link to="/login">Sign in.</Link>
  </p>
);

const renderUnavailableLayout = ({
  eyebrow,
  title,
  lead,
  detail,
  supportTitle,
  supportList,
}) => {
  // Only the network-error branch still passes steps, and it writes its own.
  // A page with nothing to pass gets no support section at all rather than a
  // default list, which used to address the owner in front of every visitor.
  const supportItems = supportList && supportList.length > 0 ? supportList : null;

  return (
    <div className="profile-unavailable-page">
      <section className="profile-unavailable-card" role="status" aria-live="polite">
        <header className="profile-unavailable-header">
          {eyebrow ? <span className="profile-unavailable-pill">{eyebrow}</span> : null}
          <h1>{title}</h1>
          <p className="profile-unavailable-lead">{lead}</p>
        </header>
        {renderUnavailableDetail(detail)}
        {supportItems ? (
          <section className="profile-unavailable-support">
            <h2>{supportTitle || "Try the following"}</h2>
            <ol className="profile-unavailable-list">
              {supportItems.map((item, index) => (
                <li key={`unavailable-support-${index}`}>
                  <span className="profile-unavailable-step" aria-hidden="true">
                    {index + 1}
                  </span>
                  <p>{item}</p>
                </li>
              ))}
            </ol>
          </section>
        ) : null}
        {renderUnavailableActions()}
        {renderUnavailableOwnerLink()}
      </section>
    </div>
  );
};

// Only the owner can reach a page that is not publicly live, so the banner
// explains why it is still a preview and links to the fix. Design 1b keeps the
// banner to a single line.
const PREVIEW_NOTICE_COPY = {
  hidden_by_owner: {
    title: "Your booking page is hidden",
    detail: "Only you can see it until you turn it back on",
    ctaLabel: "Booking page settings",
    ctaTo: "/pro/settings",
  },
  account_paused: {
    title: "Your account is paused",
    detail: "Only you can see this page until you resume it",
    ctaLabel: "Account settings",
    ctaTo: "/pro/settings/account",
  },
  account_unavailable: {
    title: "Your booking page is not live yet",
    detail: "Only you can see this page while your account is inactive",
    ctaLabel: "Account settings",
    ctaTo: "/pro/settings/account",
  },
};

// A live page whose owner still has setup left. The server counts only what
// stops a booking (services, open hours, and an address when they work from
// their own place); a missing bio is suggested but never counted.
const SETUP_NOTICE_COPY = {
  pill: "Live",
  title: "Your booking page is live",
  ctaLabel: "Finish setup",
  ctaTo: "/onboarding",
  bioTip: "Adding a short bio is recommended",
};

export const buildOwnerSetupNotice = (setup) => {
  const blocking = Array.isArray(setup?.blocking) ? setup.blocking : [];
  if (!blocking.length) return null;
  const count = blocking.length;
  const details = [
    `${count} step${count === 1 ? "" : "s"} left before clients can book`,
  ];
  if (setup?.missing?.profileDescription) details.push(SETUP_NOTICE_COPY.bioTip);
  return {
    pill: SETUP_NOTICE_COPY.pill,
    title: SETUP_NOTICE_COPY.title,
    details,
    ctaLabel: SETUP_NOTICE_COPY.ctaLabel,
    ctaTo: SETUP_NOTICE_COPY.ctaTo,
  };
};

const renderOwnerBanner = ({ pill, title, details, ctaLabel, ctaTo }) => (
  <div
    className="public-profile-preview-banner"
    role="status"
    aria-live="polite"
  >
    <p className="public-profile-preview-banner__title">
      <span className="public-profile-preview-banner__pill">{pill}</span>
      {title}
      {details.filter(Boolean).map((detail) => (
        <Fragment key={detail}>
          <span
            className="public-profile-preview-banner__sep"
            aria-hidden="true"
          >
            ·
          </span>
          <span className="public-profile-preview-banner__detail">
            {detail}
          </span>
        </Fragment>
      ))}
    </p>
    <Link className="public-profile-preview-banner__cta" to={ctaTo}>
      {ctaLabel}
    </Link>
  </div>
);

const renderOwnerPreviewBanner = (previewNotice) => {
  const copy =
    PREVIEW_NOTICE_COPY[previewNotice?.reason] ||
    PREVIEW_NOTICE_COPY.account_unavailable;
  return renderOwnerBanner({ ...copy, pill: "Preview", details: [copy.detail] });
};

// Open hours only count when a day actually has a time range on it.
const hasOpenHours = (availability) =>
  Array.isArray(availability) &&
  availability.some((day) => Array.isArray(day?.slots) && day.slots.length > 0);

// Page B. It answers a mistyped link, a retired page and a slug someone
// guessed with the same words, so that guessing cannot tell them apart.
const renderProfileNotFoundLayout = () =>
  renderUnavailableLayout({
    title: NOT_FOUND_PAGE_COPY.title,
    lead: NOT_FOUND_PAGE_COPY.lead,
  });

const ProfessionalProfile = ({
  professionalData = null,
  servicesData = null,
  availabilityData = null,
  themeOverride = null,
  contentOverrides = null,
  editorMode = false,
  // Editor only: the picture the owner has just uploaded, so the preview
  // updates without refetching the profile. `null` means "no picture".
  avatarOverride,
  onAvatarEditRequest,
  onInlineTextChange,
  onProfileContentEditRequest,
  onThemeTargetSelect,
  activeThemeTarget = "",
  // Editor only, Custom pages: { titles, selectedId, movingId, peekId,
  // onSelect, onPeek } for the name tab over each section. Without it the
  // sections draw bare.
  sectionFrames = null,
  disableBooking = false,
  // A picture of the page rather than the page (the template picker's
  // thumbnails): no owner Edit button.
  staticPreview = false,
  // Set by the illustrative booking page at /example, which passes its own
  // professionalData/servicesData/availabilityData so nothing is ever fetched.
  // The services CTA becomes a link to this href instead of opening a booking.
  demoCtaHref = "",
}) => {
  const { id, username } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const profileIdentifier = username || id || "";
  const [professional, setProfessional] = useState(null);
  const [services, setServices] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [unavailableReason, setUnavailableReason] = useState(null);
  const [previewNotice, setPreviewNotice] = useState(null);
  // Owner only: what still stops clients booking. The server never sends it to
  // anyone else.
  const [ownerSetup, setOwnerSetup] = useState(null);
  const [profileNotFound, setProfileNotFound] = useState(false);
  const [error, setError] = useState("");
  const [selectedService, setSelectedService] = useState(null);
  // Date + time chosen inline on the page before the modal opens, if any.
  const [pendingSlot, setPendingSlot] = useState(null);
  const [bookingConfirmation, setBookingConfirmation] = useState("");
  const [isBookingSubmitting, setIsBookingSubmitting] = useState(false);
  // True while a card attempt in the open booking form is holding its time.
  const [hasHeldBooking, setHasHeldBooking] = useState(false);
  // True once the client has typed or uploaded something into the open form.
  const [hasUnsavedBookingDetails, setHasUnsavedBookingDetails] = useState(false);
  const previewParam = new URLSearchParams(location.search).get("preview");
  const isPreviewMode =
    typeof previewParam === "string" &&
    ["1", "true"].includes(previewParam.trim().toLowerCase());
  // A direct link to one service (Copy link on the Services page). It can name
  // a hidden service, which the public list only returns when asked for.
  const linkedServiceId = readServiceLinkId(location.search);
  const auth = getValidToken();
  const authPayload = auth?.payload || {};

  const handleBookingSuccess = useCallback(
    ({ manageTarget }) => {
      setIsBookingSubmitting(false);
      setHasHeldBooking(false);
      setHasUnsavedBookingDetails(false);
      setSelectedService(null);
      setPendingSlot(null);
      if (manageTarget && !manageTarget.startsWith("http")) {
        // The manage page opens on its "You're booked" confirmation when it
        // gets this state, so there is nothing to show here first. The link in
        // the confirmation email carries no state and opens the plain page.
        navigate(manageTarget, { replace: true, state: { bookingConfirmed: true } });
      } else if (manageTarget) {
        setBookingConfirmation(
          "Booking confirmed! Opening your booking details..."
        );
        window.location.assign(manageTarget);
      } else {
        setBookingConfirmation(
          "Booking confirmed! Check your email for your booking details."
        );
      }
    },
    [navigate]
  );

  const handleBookingProcessingStateChange = useCallback((isProcessing) => {
    setIsBookingSubmitting(Boolean(isProcessing));
  }, []);

  const handleHeldBookingChange = useCallback((isHeld) => {
    setHasHeldBooking(Boolean(isHeld));
  }, []);

  const handleUnsavedBookingDetailsChange = useCallback((hasDetails) => {
    setHasUnsavedBookingDetails(Boolean(hasDetails));
  }, []);

  useEffect(() => {
    if (professionalData) {
      setProfessional(professionalData);
      setServices(Array.isArray(servicesData) ? servicesData : []);
      setAvailability(Array.isArray(availabilityData) ? availabilityData : []);
      setUnavailableReason(null);
      setPreviewNotice(null);
      setOwnerSetup(null);
      setProfileNotFound(false);
      setError("");
      return;
    }

    const fetchData = async () => {
      try {
        setUnavailableReason(null);
        setPreviewNotice(null);
        setOwnerSetup(null);
        setProfileNotFound(false);
        setError("");

        const requestConfig = {};
        if (isPreviewMode) {
          requestConfig.params = { preview: "1" };
        }
        // The token is what lets the API recognise an owner opening their own
        // page, which it answers with the page plus a preview notice instead of
        // the unavailable payload everyone else gets.
        const viewerAuth = getValidToken();
        if (viewerAuth?.token) {
          requestConfig.headers = {
            Authorization: `Bearer ${viewerAuth.token}`,
          };
        }

        let proRes;
        if (username) {
          try {
            proRes = await axios.get(
              `/api/professional/public/${username}`,
              requestConfig
            );
          } catch (usernameError) {
            // Fallback for legacy ID links that may still use /p/<id>.
            if ([400, 404].includes(usernameError?.response?.status)) {
              proRes = await axios.get(
                `/api/professional/${username}`,
                requestConfig
              );
            } else {
              throw usernameError;
            }
          }
        } else {
          proRes = await axios.get(`/api/professional/${id}`, requestConfig);
        }

        const resolvedProfessionalId =
          proRes?.data?.professional?._id || profileIdentifier;
        const resolvedBusiness = proRes?.data?.professional?.businessId
          || proRes?.data?.professional?.businessSlug;

        const ownerPreview = proRes?.data?.preview?.isOwnerPreview
          ? proRes.data.preview
          : null;

        setProfessional(proRes.data.professional);
        setAvailability(proRes.data.availability);
        setPreviewNotice(ownerPreview);
        setOwnerSetup(proRes?.data?.setup || null);

        try {
          const servicesUrl = resolvedBusiness
            ? `/api/services/by-business/${resolvedBusiness}`
            : `/api/services/by-user/${resolvedProfessionalId}`;
          const serviceRes = linkedServiceId
            ? await axios.get(servicesUrl, { params: { include: linkedServiceId } })
            : await axios.get(servicesUrl);
          setServices(serviceRes.data);
        } catch (serviceError) {
          const serviceStatus = serviceError?.response?.status;
          // A previewing owner can be paused or inactive, which the public
          // services endpoint answers with 423; the preview just shows none.
          const toleratedInPreview = Boolean(ownerPreview) && serviceStatus === 423;
          if (serviceStatus !== 404 && !toleratedInPreview) {
            throw serviceError;
          }
          setServices([]);
        }
      } catch (err) {
        const status = err?.response?.status;
        const reason = err?.response?.data;
        // The only thing the public body carries now is a reason code, and it
        // is the code alone that picks the page.
        const publicReason =
          typeof reason?.reason === "string" ? reason.reason.trim() : "";
        // A hidden page answers 423/"away" now, but a cached or older response
        // can still arrive in the pre-reason-code shape. It belongs on the same
        // page either way, so keep reading the old fields as a fallback.
        const isHiddenByOwner =
          reason?.accountStatus === "hidden_by_owner" ||
          reason?.visibilityReason === "hidden_by_owner";

        if (publicReason === "away" || isHiddenByOwner) {
          setUnavailableReason({ reason: "away" });
          return;
        }

        if (
          publicReason === "not_found" ||
          [400, 404, 410].includes(status) ||
          status === 403 ||
          status === 423
        ) {
          setProfileNotFound(true);
          return;
        }

        setError("This profile is temporarily unavailable. Please try again later.");
        console.error("Error fetching profile:", err);
      }
    };
    fetchData();
  }, [
    availabilityData,
    id,
    isPreviewMode,
    linkedServiceId,
    professionalData,
    profileIdentifier,
    servicesData,
    username,
  ]);

  useEffect(() => {
    // A preview is already explained by its banner, so the owner never falls
    // through to the unavailable page their visitors see.
    if (!professional || unavailableReason || previewNotice || editorMode) return;

    const professionalStatus = String(
      professional?.accountStatus || professional?.status || ""
    )
      .trim()
      .toLowerCase();

    // A safety net for a payload that slipped past the server's own block. It
    // reads the status only to decide whether to stop, never to say it out
    // loud: the visitor gets the same page, and the same words, as a pause.
    if (professionalStatus && professionalStatus !== "active") {
      setUnavailableReason({ reason: "away" });
    }
  }, [editorMode, previewNotice, professional, unavailableReason]);

  // Neither fallback page is a page worth indexing, and neither may be titled
  // after the professional, so the tab says as little as the card does. Both
  // are undone on the way out so a live profile is never left noindexed.
  const isFallbackPage = profileNotFound || Boolean(unavailableReason);
  useEffect(() => {
    if (!isFallbackPage || typeof document === "undefined") return undefined;

    const previousTitle = document.title;
    document.title = FALLBACK_DOCUMENT_TITLE;

    const robots = document.createElement("meta");
    robots.setAttribute("name", "robots");
    robots.setAttribute("content", "noindex, nofollow");
    document.head.appendChild(robots);

    return () => {
      document.title = previousTitle;
      robots.remove();
    };
  }, [isFallbackPage]);

  // Load only this page's font, rather than every font a pro could pick. The
  // owner banners are set in Inter whatever the page's font is. Worked out the
  // same way as mergedTheme below, which is only built after the early returns.
  const themeFontFamily = {
    ...(professional?.theme || {}),
    ...(themeOverride || {}),
  }.fontFamily;
  const showsOwnerBanner = Boolean(previewNotice || buildOwnerSetupNotice(ownerSetup));
  useEffect(() => {
    if (!professional || isFallbackPage) return;
    loadProfileThemeFont(themeFontFamily);
    if (showsOwnerBanner) loadProfileThemeFont("Inter");
  }, [isFallbackPage, professional, showsOwnerBanner, themeFontFamily]);

  if (profileNotFound && !editorMode) {
    return renderProfileNotFoundLayout();
  }

  // Page A. Every "away" reason the server distinguishes — a live pause, a
  // scheduled one, a page the owner switched off — lands here on the same copy.
  if (unavailableReason && !editorMode) {
    return renderUnavailableLayout(AWAY_PAGE_COPY);
  }

  if (error) {
    return renderUnavailableLayout({
      eyebrow: "Unavailable",
      title: "We couldn't load this profile",
      lead: error,
      detail:
        "We ran into a temporary issue while trying to open this profile. Please refresh the page or come back in a few minutes.",
      supportTitle: "Try the following",
      supportList: [
        "Refresh the page or return home before attempting again.",
        "Check your internet connection or try from a different device.",
        <>Contact <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> if this continues.</>,
      ],
    });
  }

  if (!professional) return <p>Loading...</p>;

  const firstName = professional.name?.split(" ")[0] || "This professional";
  const hasCustomName = firstName !== "This professional";
  const paymentMode = resolveProfessionalPaymentMode(professional);
  const isCashOnly = paymentMode === "cash_only";
  const hasBusinessManagedPayments = services.some(
    (service) => service?.paymentRouting?.payoutMode === "business_managed",
  );
  const effectiveCashOnly = isCashOnly && !hasBusinessManagedPayments;
  const resolvedProfessionalId = professional?._id || profileIdentifier;
  const authRole = normalizeIdentifier(authPayload?.role);
  const viewerId = normalizeIdentifier(authPayload?._id || authPayload?.id);
  const viewerUsername = normalizeIdentifier(authPayload?.username);
  const professionalId = normalizeIdentifier(professional?._id);
  const professionalUsername = normalizeIdentifier(professional?.username);
  const normalizedProfileIdentifier = normalizeIdentifier(profileIdentifier);
  const isLoggedInWithEditRole =
    Boolean(auth?.token) && canUseProfessionalFeatures(authRole);
  const idMatches = Boolean(
    viewerId &&
      (professionalId ? viewerId === professionalId : viewerId === normalizedProfileIdentifier)
  );
  const usernameMatches = Boolean(
    viewerUsername &&
      (professionalUsername
        ? viewerUsername === professionalUsername
        : viewerUsername === normalizedProfileIdentifier)
  );
  const canEditOwnPublicProfile =
    !editorMode && !staticPreview && isLoggedInWithEditRole && (idMatches || usernameMatches);
  const businessNameCandidate =
    typeof contentOverrides?.businessName === "string"
      ? contentOverrides.businessName
      : professional?.businessName;
  const businessNameValue = businessNameCandidate?.trim() || "";
  const taglineCandidate =
    typeof contentOverrides?.tagline === "string"
      ? contentOverrides.tagline
      : professional?.tagline;
  const taglineValue = taglineCandidate?.trim() || "";
  // Business name, then the person's name, and only then the tagline. Letting
  // the tagline into the heading is what used to swallow it: a pro with no
  // business name lost the tagline line entirely, because it had become the h1.
  const pageTitle =
    businessNameValue ||
    professional?.name?.trim() ||
    taglineValue ||
    "Booking page";
  // The tagline only earns its own line when the heading is not already showing it.
  const heroSubtitle = taglineValue && taglineValue !== pageTitle ? taglineValue : "";
  const avatarUrl = resolveAvatarUrl(
    avatarOverride === undefined ? professional?.image : avatarOverride,
  );
  const avatarAlt = professional?.name?.trim() || pageTitle;
  const avatarInitial = getAvatarInitial(
    businessNameValue,
    professional?.name,
    taglineValue,
    pageTitle,
  );
  const heroTags = buildHeroTags({
    professional,
    availability,
    services,
    isCashOnly: effectiveCashOnly,
  });

  // A bio is optional: without one the public page simply has no bio. The
  // editor keeps a stand-in so the owner still has something to click to add one.
  const editorAboutPlaceholder = `Expect a collaborative, transparent experience from ${hasCustomName ? firstName : "this professional"
    } with updates before, during, and after your appointment.`;
  const rawAboutCopy =
    (typeof contentOverrides?.bio === "string"
      ? contentOverrides.bio
      : professional?.bio)?.trim() ||
    professional?.about?.trim() ||
    "";
  // A rich-text editor can leave "<p><br></p>" behind; that is still no bio.
  const ownAboutCopy =
    /<img\b/i.test(rawAboutCopy) ||
    rawAboutCopy.replace(/<[^>]*>/g, " ").replace(/&nbsp;/gi, " ").trim()
      ? rawAboutCopy
      : "";
  const aboutCopy = ownAboutCopy || (editorMode ? editorAboutPlaceholder : "");
  const aboutCopyHtml = aboutCopy
    ? createSanitizedMarkup(aboutCopy, { allowFontSize: true })
    : null;
  const businessLabel = businessNameValue || professional?.name?.trim() || "";
  const acceptingBookings = hasOpenHours(availability);
  const proLocationBookable = professional?.proLocationBookable !== false;
  // The account banner wins: a paused or hidden page is the bigger news.
  const ownerSetupNotice = previewNotice ? null : buildOwnerSetupNotice(ownerSetup);
  const galleryItems = normalizeGalleryItems(professional?.gallery);

  const mergedTheme = {
    ...(professional?.theme || {}),
    ...(themeOverride || {}),
  };
  const servicesHeading =
    typeof contentOverrides?.servicesHeading === "string"
      ? contentOverrides.servicesHeading
      : mergedTheme.servicesHeading;
  const primaryCtaText =
    typeof contentOverrides?.primaryCtaText === "string"
      ? contentOverrides.primaryCtaText
      : mergedTheme.primaryCtaText;
  // The design puts the cancellation terms under the booking CTA, where a
  // client reads them just before committing, rather than in a hero chip.
  const cancellationPolicyOption = getCancellationPolicyOption(
    professional?.cancellationPolicy,
  );
  const cancellationNote = `${cancellationPolicyOption.label} cancellation policy. ${cancellationPolicyOption.description}`;
  const isCustomPage = isCustomProfileFormat(professional);
  const themeConfig = buildPublicProfileTheme(mergedTheme, { isCustom: isCustomPage });
  const profileAppearance = themeConfig.cssVariables;
  const handleEditorSurfaceClick = (event) => {
    if (!editorMode || typeof onThemeTargetSelect !== "function") return;
    const closestTarget = event.target
      ?.closest?.("[data-theme-target]")
      ?.getAttribute("data-theme-target");
    onThemeTargetSelect(closestTarget || "page");
  };
  const handleInlineTextFieldChange = (field, value) => {
    if (!editorMode) return;
    onInlineTextChange?.(field, value);
  };

  // The page's sections. A Standard page draws all three in this order; a
  // Custom page draws them in the order of its saved list. The booking pop-up
  // stays with the page: the services section asks for it through
  // onBookService.
  // A Custom page's header can be the pro's own images instead of the name
  // and picture ("Stacked photos"). Without any images it stays as usual.
  const headerImages =
    themeConfig.headerLayout === "images"
      ? normalizeIntroImages(professional?.headerImages, { businessName: businessLabel })
      : [];
  const renderHeaderSection = () => (
    <ProfileHeaderSection
      headerImages={headerImages}
      editorMode={editorMode}
      pageTitle={pageTitle}
      heroSubtitle={heroSubtitle}
      heroTags={heroTags}
      aboutCopyHtml={aboutCopyHtml}
      avatarUrl={avatarUrl}
      avatarAlt={avatarAlt}
      avatarInitial={avatarInitial}
      onAvatarEditRequest={onAvatarEditRequest}
      onInlineTextChange={handleInlineTextFieldChange}
      onThemeTargetSelect={onThemeTargetSelect}
      onProfileContentEditRequest={onProfileContentEditRequest}
    />
  );

  const renderGallerySection = () => (
    <ProfileGallerySection items={galleryItems} editorMode={editorMode} />
  );

  // Only a Custom page has an Intro; it reads its words from the section and
  // its images from the pro's intro image list.
  const renderIntroSection = (section) => (
    <ProfileIntroSection
      content={section?.content}
      images={normalizeIntroImages(professional?.introImages, {
        businessName: businessLabel,
      })}
      businessName={businessLabel}
    />
  );

  // Policies, Usual hours, Contact and Questions are Custom-only too. Each
  // draws nothing when it has nothing to show.
  const renderPoliciesSection = (section) => (
    <ProfilePoliciesSection
      sectionId={section?.id}
      content={section?.content}
      cancellationPolicy={professional?.cancellationPolicy}
    />
  );

  const renderHoursSection = (section) => (
    <ProfileHoursSection
      sectionId={section?.id}
      content={section?.content}
      availability={availability}
      timeZone={professional?.timeZone}
    />
  );

  // Never the address: only these four, and on a team page the server sends
  // the business phone as contactPhone.
  const renderContactSection = (section) => (
    <ProfileContactSection
      sectionId={section?.id}
      content={section?.content}
      contact={{
        phone: professional?.contactPhone,
        website: professional?.website,
        instagramHandle: professional?.instagramHandle,
        tiktokHandle: professional?.tiktokHandle,
      }}
    />
  );

  const renderFaqSection = (section) => (
    <ProfileFaqSection sectionId={section?.id} content={section?.content} />
  );

  const handleBookService = (service, slot) => {
    if (editorMode || disableBooking) {
      return;
    }
    setBookingConfirmation("");
    setIsBookingSubmitting(false);
    setHasHeldBooking(false);
    setHasUnsavedBookingDetails(false);
    setPendingSlot(slot?.date && slot?.timeSlot ? slot : null);
    setSelectedService(service);
  };

  const renderServicesSection = () => (
    <ProfileServicesSection
      services={services}
      className={themeConfig.servicesClassName}
      firstName={firstName}
      hasCustomName={hasCustomName}
      paymentMode={paymentMode}
      servicesHeading={servicesHeading}
      ctaText={primaryCtaText}
      cancellationNote={cancellationNote}
      isEditorMode={editorMode}
      activeThemeTarget={activeThemeTarget}
      onThemeTargetSelect={onThemeTargetSelect}
      onInlineTextChange={handleInlineTextFieldChange}
      professionalId={resolvedProfessionalId}
      availability={availability}
      bookingDisabled={disableBooking}
      demoCtaHref={demoCtaHref}
      acceptingBookings={acceptingBookings}
      proLocationBookable={proLocationBookable}
      businessLabel={businessLabel}
      initialServiceId={linkedServiceId}
      showCategories={professional?.showServiceCategories === true}
      layout={themeConfig.servicesLayout}
      onBookService={handleBookService}
    />
  );

  return (
    <div
      className={[
        "professional-profile-page",
        themeConfig.classes.backgroundShape,
        themeConfig.classes.backgroundShapeDensity,
        editorMode ? "is-theme-editor-mode" : "",
        previewNotice || ownerSetupNotice ? "has-owner-preview-banner" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={profileAppearance}
      data-theme-target={editorMode ? "page" : undefined}
      data-active-theme-target={editorMode ? activeThemeTarget || "" : undefined}
      onClick={handleEditorSurfaceClick}
    >
      {previewNotice && renderOwnerPreviewBanner(previewNotice)}
      {ownerSetupNotice && renderOwnerBanner(ownerSetupNotice)}
      {canEditOwnPublicProfile && (
        <button
          type="button"
          className="public-profile-owner-edit-button"
          onClick={() => navigate("/booking-page-visual-editor")}
          aria-label="Edit booking page"
        >
          <svg
            className="public-profile-owner-edit-button__icon"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M13.6 3.4a1.9 1.9 0 1 1 2.7 2.7L8.1 14.3l-3.4.7.7-3.4 8.2-8.2Z"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>Edit</span>
        </button>
      )}
      <div className="professional-profile">
        {bookingConfirmation && !editorMode && !disableBooking && (
          <div
            className="booking-confirm-banner"
            role="status"
            aria-live="polite"
          >
          {bookingConfirmation}
        </div>
        )}

        {/* One decoration layer for the whole page, not per section, so the
            shapes carry down the page and clip against its outer edges. */}
        <div className="profile-deco-layer" aria-hidden="true">
          <span className="profile-deco profile-deco--1" />
          <span className="profile-deco profile-deco--2" />
          <span className="profile-deco profile-deco--3" />
          <span className="profile-deco profile-deco--4" />
          <span className="profile-deco profile-deco--5" />
          <span className="profile-deco profile-deco--6" />
        </div>

        {isCustomPage ? (
          renderPageSections(
            resolvePageSections(professional?.pageSections),
            {
              header: renderHeaderSection,
              intro: renderIntroSection,
              gallery: renderGallerySection,
              services: renderServicesSection,
              policies: renderPoliciesSection,
              hours: renderHoursSection,
              contact: renderContactSection,
              faq: renderFaqSection,
            },
            editorMode ? sectionFrames : null,
          )
        ) : (
          <>
            {renderHeaderSection()}
            {renderGallerySection()}
            {renderServicesSection()}
          </>
        )}

        {/* Booking Modal */}
        {!editorMode && !disableBooking && (
          <Modal
            open={!!selectedService}
            onClose={() => {
              if (isBookingSubmitting) return;
              // One question at most: the held time is the bigger loss, so
              // its warning wins when both apply.
              const closeConfirmation = hasHeldBooking
                ? HELD_BOOKING_CLOSE_CONFIRMATION
                : hasUnsavedBookingDetails
                  ? UNSAVED_BOOKING_CLOSE_CONFIRMATION
                  : "";
              if (
                closeConfirmation &&
                typeof window.confirm === "function" &&
                !window.confirm(closeConfirmation)
              ) {
                return;
              }
              setIsBookingSubmitting(false);
              setHasHeldBooking(false);
              setHasUnsavedBookingDetails(false);
              setSelectedService(null);
              setPendingSlot(null);
            }}
            closeDisabled={isBookingSubmitting}
            title={selectedService ? `Book: ${selectedService.title}` : "Book"}
          >
            {selectedService && (
              <Suspense fallback={<p role="status">Loading the booking form…</p>}>
                <BookingForm
                  professionalId={resolvedProfessionalId}
                  service={selectedService}
                  professionalZip={professional?.location?.zip || ""}
                  proLocationBookable={proLocationBookable}
                  businessName={
                    professional?.businessName ||
                    professional?.tagline ||
                    professional?.name ||
                    ""
                  }
                  cancellationPolicy={professional?.cancellationPolicy}
                  availability={availability}
                  initialDate={pendingSlot?.date || ""}
                  initialTimeSlot={pendingSlot?.timeSlot || ""}
                  canAcceptPayments={
                    selectedService?.paymentRouting?.payoutMode === "business_managed" ||
                    !effectiveCashOnly
                  }
                  paymentMode={resolveServicePaymentMode(selectedService, paymentMode)}
                  onSuccess={handleBookingSuccess}
                  onProcessingStateChange={handleBookingProcessingStateChange}
                  onHeldBookingChange={handleHeldBookingChange}
                  onUnsavedDetailsChange={handleUnsavedBookingDetailsChange}
                  servicesLayout={
                    themeConfig.servicesLayout === "minimal" || themeConfig.servicesLayout === "list"
                      ? "minimal"
                      : "card"
                  }
                />
              </Suspense>
            )}
          </Modal>
        )}
      </div>
    </div>
  );
};

export default ProfessionalProfile;
